import React, { useState, useMemo, useEffect } from 'react'
import {
  Flex,
  SimpleGrid,
  Box,
  Button,
  MenuButton,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  Checkbox,
  MenuItem,
  MenuList,
  Menu,
} from '@chakra-ui/react'

import Header from '../../../app/components/Header'
import { useGetOverviewDeviceDataQuery } from '../api/dasboardApi'
import { PeriodTabStrip } from '../components/PeriodTabStrip'
import { convertToKW, PERIODPARAMS } from '../consts'
import { useQueryParams } from '../hooks/useQueryParams'
import { organizeParameters, Parameter } from '../../../utils/helpers'
import { LineChartCardGrouped } from './lineChart/LineChartCardGrouped'
import { LineChartCard } from './lineChart/LineChartCard'
import { ArrowBackIcon } from '@chakra-ui/icons'

const DEFAULT_PARAMS = new Set([
  'Voltage',
  'ActivePower',
  'Current',
  'TotalConsumedEnergy',
  'PowerFactor',
])

export function OverviewDevice() {
  const [queryParams] = useQueryParams(PERIODPARAMS)

  const { data } = useGetOverviewDeviceDataQuery(
    {
      from_date: queryParams.from,
      to_date: queryParams.to,
      device: queryParams.device_id,
    },
    { skip: !queryParams.from || !queryParams.to },
  )

  const organized = useMemo(() => {
    if (!data || !Array.isArray(data.parameters) || data.parameters.length === 0)
      return []
    return organizeParameters(data.parameters)
  }, [data])

  const [selectedParams, setSelectedParams] = useState<Record<string, Set<string>>>({})

  useEffect(() => {
    if (organized.length === 0) return

    const initial: Record<string, Set<string>> = {}
    const allParams: { group: string; title: string }[] = []

    organized.forEach((entry) => {
      if (entry.type === 'grouped') {
        entry.parameters.forEach((p) => {
          allParams.push({ group: entry.title, title: p.parameter_title })
        })
      } else if (entry.type === 'single') {
        allParams.push({ group: '__single__', title: entry.parameter.parameter_title })
      }
    })

    const matchingDefaults = allParams.filter((p) => DEFAULT_PARAMS.has(p.title))
    const selected =
      matchingDefaults.length > 0 ? matchingDefaults : allParams.slice(0, 6)

    selected.forEach(({ group, title }) => {
      if (!initial[group]) {
        initial[group] = new Set()
      }
      initial[group].add(title)
    })

    setSelectedParams(initial)
  }, [organized])

  const toggleParam = (groupTitle: string, paramTitle: string) => {
    setSelectedParams((prev) => {
      const group = new Set(prev[groupTitle] ?? [])
      if (group.has(paramTitle)) {
        group.delete(paramTitle)
      } else {
        group.add(paramTitle)
      }
      return {
        ...prev,
        [groupTitle]: group,
      }
    })
  }

  const HeaderContent = <Header title={queryParams.title || 'Device Overview'} />

  return (
    <>
      <Button
        leftIcon={<ArrowBackIcon />}
        colorScheme="gray"
        variant="ghost"
        mb={4}
        padding={2}
        justifyContent="start"
        onClick={() => window.history.back()}
      >
        Back to overview
      </Button>
      {HeaderContent}
      <Flex direction="column" gap={5}>
        <Flex justifyContent="space-between" alignItems="center" mt={4}>
          <PeriodTabStrip
            period={queryParams.period}
            from={queryParams.from}
            to={queryParams.to}
          />
          {data?.parameters.length > 0 && (
            <Menu closeOnSelect={false}>
              <MenuButton as={Button} size="sm">
                Select Parameters
              </MenuButton>
              <MenuList maxHeight="300px" overflowY="auto">
                {organized.map((entry) => {
                  if (entry.type === 'grouped') {
                    const allSelected = entry.parameters.every((p) =>
                      selectedParams[entry.title]?.has(p.parameter_title),
                    )

                    const toggleAll = () => {
                      setSelectedParams((prev) => ({
                        ...prev,
                        [entry.title]: allSelected
                          ? new Set()
                          : new Set(entry.parameters.map((p) => p.parameter_title)),
                      }))
                    }

                    return (
                      <Box key={entry.title} px={3} py={1}>
                        <Flex justify="space-between" align="center">
                          <Text fontSize="sm" fontWeight="bold" mt={2} mb={1}>
                            {entry.title}
                          </Text>
                          <Button size="xs" variant="ghost" onClick={toggleAll}>
                            {allSelected ? 'None' : 'All'}
                          </Button>
                        </Flex>
                        {entry.parameters.map((param) => (
                          <MenuItem key={param.parameter_title} py={1}>
                            <Checkbox
                              isChecked={selectedParams[entry.title]?.has(
                                param.parameter_title,
                              )}
                              onChange={() =>
                                toggleParam(entry.title, param.parameter_title)
                              }
                            >
                              {param.parameter_title}
                            </Checkbox>
                          </MenuItem>
                        ))}
                      </Box>
                    )
                  }
                })}
                <Box key="__single__">
                  {organized
                    .filter(
                      (e): e is { type: 'single'; parameter: Parameter } =>
                        e.type === 'single',
                    )
                    .map((e) => e.parameter.parameter_title)
                    .filter((title, idx, arr) => arr.indexOf(title) === idx) // Remove duplicates if any
                    .map((title) => (
                      <MenuItem key={title} py={1}>
                        <Checkbox
                          isChecked={selectedParams.__single__?.has(title)}
                          onChange={() => toggleParam('__single__', title)}
                        >
                          {title}
                        </Checkbox>
                      </MenuItem>
                    ))}
                </Box>
              </MenuList>
            </Menu>
          )}
        </Flex>

        {data?.parameters?.length === 0 ? (
          <Alert
            status="info"
            bg="blue.50"
            borderRadius="md"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
            color="blue.800"
          >
            <AlertIcon boxSize="30px" mr={0} color="blue.500" />
            <AlertTitle mt={2} mb={1} fontSize="md" fontWeight="bold">
              No data available for the selected period
            </AlertTitle>
          </Alert>
        ) : Object.values(selectedParams).every((set) => set.size === 0) ? (
          <Alert
            status="info"
            bg="blue.50"
            borderRadius="md"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
            color="blue.800"
          >
            <AlertIcon boxSize="30px" mr={0} color="blue.500" />
            <AlertTitle mt={2} mb={1} fontSize="md" fontWeight="bold">
              No parameters selected. Please choose one or more to display charts.
            </AlertTitle>
          </Alert>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mt={4}>
            {organized.map((entry) => {
              if (entry.type === 'single') {
                const isVisible = selectedParams.__single__?.has(
                  entry.parameter.parameter_title,
                )
                if (!isVisible) return null

                return (
                  <LineChartCard
                    key={entry.parameter.parameter_title}
                    title={entry.parameter.parameter_title}
                    unit={entry.parameter.measurement_unit}
                    value={convertToKW(
                      entry.parameter.value,
                      entry.parameter.measurement_unit,
                    )}
                    previousValue={entry.parameter.previous_value}
                    currentPeriod={entry.parameter.period}
                    previousPeriod={entry.parameter.previous_period}
                  />
                )
              }

              const visibleParameters = entry.parameters.filter((p) =>
                selectedParams[entry.title]?.has(p.parameter_title),
              )

              if (visibleParameters.length === 0) return null

              return (
                <LineChartCardGrouped key={entry.title} parameters={visibleParameters} />
              )
            })}
          </SimpleGrid>
        )}
      </Flex>
    </>
  )
}
